import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, Check, CheckCircle2, ShieldCheck, FileWarning } from 'lucide-react';
import { PRIMARY_CTA, DEMO_CTA } from './data';

// Where the readiness CTAs point. "Check My Draw" starts the in-app flow;
// "See How DrawReady Works" goes to the primary authority page.
const CHECK_CTA = { label: 'Check My Draw', href: '/signup' };
const SEE_HOW = { label: 'See How DrawReady Works', href: '/construction-draw-management-software' };

// ------------------------------------------------------------------
// DrawReady Pre-Submission Check — interactive, manual checklist.
// Educational only: never claims completing it guarantees approval.
// ------------------------------------------------------------------
const PRESUBMISSION_ITEMS = [
  'Draw amount confirmed',
  'Project budget updated',
  'Current progress updated',
  'Invoices uploaded',
  'Receipts captured',
  'Required lien waivers collected',
  'Required compliance documents collected',
  'Change orders reflected',
  'Previous draw reconciled',
  'Supporting project/site photos ready',
  'Lender-specific requirements reviewed',
];

export function DrawReadyPreSubmissionCheck() {
  const [checked, setChecked] = React.useState(() => new Set());
  const toggle = (i) =>
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  const done = checked.size;
  const total = PRESUBMISSION_ITEMS.length;

  return (
    <section className="my-10 rounded-2xl border border-slate-200 overflow-hidden" data-testid="presubmission-check">
      <div className="bg-slate-900 text-white px-6 py-5">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-blue-300">
          <ShieldCheck className="w-4 h-4" /> Pre-Submission Check
        </div>
        <h3 className="text-xl font-extrabold mt-1">DrawReady Pre-Submission Check</h3>
        <p className="text-slate-300 text-sm mt-1">
          Walk your draw package before it reaches your lender. Check each item you&apos;ve handled.
        </p>
      </div>

      <div className="px-6 py-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-slate-700">{done} of {total} reviewed</span>
          <div className="h-2 w-32 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all" style={{ width: `${(done / total) * 100}%` }} />
          </div>
        </div>

        <ul className="space-y-2">
          {PRESUBMISSION_ITEMS.map((label, i) => {
            const isOn = checked.has(i);
            return (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  data-testid={`presubmission-item-${i}`}
                  aria-pressed={isOn}
                  className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                    isOn ? 'border-emerald-300 bg-emerald-50/70' : 'border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <span
                    className={`flex-shrink-0 w-5 h-5 rounded-md border flex items-center justify-center ${
                      isOn ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 bg-white'
                    }`}
                  >
                    {isOn && <Check className="w-3.5 h-3.5 text-white" />}
                  </span>
                  <span className={`text-sm font-medium ${isOn ? 'text-slate-500 line-through' : 'text-slate-800'}`}>
                    {label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <p className="text-xs text-slate-400 mt-4">
          This checklist is for planning only. Requirements vary by lender, and completing it does not
          guarantee your draw will be approved.
        </p>
      </div>

      <div className="border-t border-slate-100 bg-blue-50/50 px-6 py-5">
        <p className="text-lg font-bold text-slate-900">Checked everything manually?</p>
        <p className="text-slate-600 mt-1">
          DrawReady gives your project a live Draw Readiness Score so you can see what&apos;s missing before you submit.
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <Link
            to={CHECK_CTA.href}
            data-testid="presubmission-check-cta"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-full px-5 py-2.5"
          >
            {CHECK_CTA.label} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to={SEE_HOW.href}
            data-testid="presubmission-seehow-cta"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 bg-white hover:bg-slate-100 border border-slate-200 rounded-full px-5 py-2.5"
          >
            {SEE_HOW.label}
          </Link>
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------
// Construction draw process visual — responsive 8-step flow.
// ------------------------------------------------------------------
const PROCESS_STEPS = [
  'Work progresses',
  'Costs & documents are collected',
  'Budget and progress are updated',
  'Draw package is prepared',
  'Requirements are checked',
  'Draw is submitted',
  'Lender reviews / inspection occurs if required',
  'Funds are released after approval',
];

export function DrawProcessVisual() {
  return (
    <section className="my-10 rounded-2xl border border-slate-200 p-6" data-testid="draw-process-visual">
      <h3 className="text-lg font-extrabold text-slate-900">How a construction draw moves</h3>
      <p className="text-sm text-slate-500 mt-1">
        A typical path from completed work to released funds. Every lender is different — some steps may
        vary or overlap.
      </p>
      <ol className="mt-5 space-y-0">
        {PROCESS_STEPS.map((step, i) => (
          <li key={i}>
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <span className="text-sm font-semibold text-slate-800">{step}</span>
            </div>
            {i < PROCESS_STEPS.length - 1 && (
              <div className="flex justify-center py-1.5" aria-hidden="true">
                <ArrowDown className="w-4 h-4 text-slate-300" />
              </div>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

// ------------------------------------------------------------------
// DrawReady relief visual — manual prep vs DrawReady comparison.
// ------------------------------------------------------------------
const MANUAL_INPUTS = ['Receipts', 'Invoices', 'Budget spreadsheet', 'Lien waivers', 'Progress', 'Loan information', 'Site photos', 'Emails'];
const MANUAL_FLOW = ['YOU reconcile everything', 'Submit', 'Hope nothing is missing'];
const DR_INPUTS = ['Project costs', 'Budget', 'Progress', 'Receipts', 'Compliance documents', 'Draw history', 'Loan information'];
const DR_FLOW = ['Draw Readiness Score', 'Fix what\u2019s missing', 'Submit bank-ready draw package'];

function FlowSteps({ steps, tone }) {
  return (
    <div className="mt-4 space-y-1.5">
      {steps.map((s, i) => (
        <div key={i}>
          <div className={`rounded-lg px-3 py-2 text-sm font-semibold ${tone === 'dr' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
            {s}
          </div>
          {i < steps.length - 1 && (
            <div className="flex justify-center py-0.5" aria-hidden="true">
              <ArrowDown className={`w-3.5 h-3.5 ${tone === 'dr' ? 'text-blue-300' : 'text-slate-300'}`} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function DrawReadyReliefVisual() {
  return (
    <section className="my-10" data-testid="relief-visual">
      <div className="grid md:grid-cols-2 gap-4">
        {/* Manual */}
        <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-5">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-rose-500">
            <FileWarning className="w-4 h-4" /> Manual draw prep
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {MANUAL_INPUTS.map((x) => (
              <span key={x} className="text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-full px-2.5 py-1">{x}</span>
            ))}
          </div>
          <FlowSteps steps={MANUAL_FLOW} tone="manual" />
        </div>
        {/* DrawReady */}
        <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-5">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-blue-600">
            <CheckCircle2 className="w-4 h-4" /> DrawReady
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {DR_INPUTS.map((x) => (
              <span key={x} className="text-xs font-medium text-slate-700 bg-white border border-blue-200 rounded-full px-2.5 py-1">{x}</span>
            ))}
          </div>
          <FlowSteps steps={DR_FLOW} tone="dr" />
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-3">
        DrawReady helps you organize and verify your draw before submission. It does not guarantee lender approval.
      </p>
    </section>
  );
}

// ------------------------------------------------------------------
// Aspirin inline CTA — pain -> relief -> action.
// ------------------------------------------------------------------
export function AspirinInlineCta() {
  return (
    <div className="my-8 rounded-2xl border border-blue-200 bg-blue-50/60 p-6" data-testid="aspirin-inline-cta">
      <p className="text-lg font-bold text-slate-900">Don&apos;t find out what&apos;s missing after you submit.</p>
      <p className="text-slate-600 mt-1">DrawReady shows you what your draw still needs before it reaches your lender.</p>
      <Link
        to={CHECK_CTA.href}
        data-testid="aspirin-inline-cta-btn"
        className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-full px-5 py-2.5"
      >
        {CHECK_CTA.label} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

// ------------------------------------------------------------------
// End-of-article CTA — primary + secondary.
// ------------------------------------------------------------------
export function EndArticleCta() {
  return (
    <div className="mt-12 rounded-3xl bg-slate-900 text-white p-8 md:p-10 text-center" data-testid="end-article-cta">
      <h2 className="text-2xl md:text-3xl font-extrabold">Your project needs the next draw to keep moving.</h2>
      <p className="text-slate-300 mt-2 max-w-xl mx-auto">
        Get your budget, progress, receipts and documentation organized before you submit.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
        <Link
          to={PRIMARY_CTA.href}
          data-testid="end-article-primary-cta"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-full px-6 py-3"
        >
          {PRIMARY_CTA.label} <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to={DEMO_CTA.href}
          data-testid="end-article-secondary-cta"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 bg-white hover:bg-slate-100 rounded-full px-6 py-3"
        >
          {DEMO_CTA.label}
        </Link>
      </div>
    </div>
  );
}
